import { Component, OnInit } from '@angular/core';
import { JsonService } from '../_services/json.service';
import { Router } from '@angular/router';
import { ConfirmationDialogService } from '../_services/confirmation-dialog.service';
import { DialogService } from '../dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../_services/logger.service';
import { LoadingService } from '../_services/loading.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-pdv-list',
  templateUrl: './pdv-list.component.html',
  styleUrl: './pdv-list.component.scss'
})
export class PdvListComponent implements OnInit {
  public jsonPaises: Array<any> = [];
  public listaItens: Array<any> = [];
  public listaShowItens: Array<any> = [];
  public currentPage = 1;

  public filtro: any = {
    pais: '',
    cidade: '',
    codPdv: '',
    search: '',
    status: 'ativos'
  };

  // Upload de PDVs (modal)
  public isUploadModalOpen: boolean = false;
  public uploadCountryId: string = '';
  public selectedPdvFile: File | null = null;
  public selectedPdvFileCount: number | null = null;
  public uploadingPdv: boolean = false;

  constructor(
    private jsonService: JsonService,
    private confirmationDialog: ConfirmationDialogService,
    private dialogService: DialogService,
    private router: Router,
    private logger: LoggerService,
    private loadingService: LoadingService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadPdvs();

    this.jsonService.listCountry().subscribe(
      resp => {
        this.jsonPaises = resp;
      },
      erro => {
        this.logger.error('Erro ao carregar países', erro);
      }
    );
  }

  loadPdvs(filtra: boolean = false) {
    this.jsonService.getPdvList(undefined, undefined, this.isActiveFromStatus()).subscribe(
      resp => {
        this.listaItens = resp;
        this.refreshList(filtra);
      },
      erro => {
        this.logger.error('Erro ao carregar PDVs', erro);
      }
    );
  }

  private isActiveFromStatus(): boolean | undefined {
    if (this.filtro.status === 'ativos') return true;
    if (this.filtro.status === 'inativos') return false;
    return undefined; // 'todos'
  }

  consultar(): void {
    // Status muda o conjunto de dados no servidor — recarrega e reaplica os demais filtros.
    this.loadPdvs(true);
  }

  private refreshList(filtra: boolean = false) {
    if (filtra) {
      this.atualizaFiltro();
    } else {
      this.listaShowItens = this.listaItens;
    }
  }

  editarPdv(item: any) {
    this.router.navigate(['/pdv-detalhe', item.id]);
  }

  adicionarPdv() {
    this.router.navigate(['/pdv-detalhe', 'novo']);
  }

  async removerPdv(item: any) {
    let title = this.translate.instant('Êxito') + '!';
    let texto = this.translate.instant('Tem certeza de que deseja excluir este PDV?');
    const confirmed = await this.confirmationDialog.confirm(title, texto);

    if (confirmed) {
      const formData = new FormData();
      formData.append('id', item.id);
      this.jsonService.deletePdv(formData).subscribe(
        () => {
          let title = this.translate.instant('Êxito') + '!';
          let texto = this.translate.instant('PDV excluído com sucesso!');
          this.dialogService.abrirDialog('aviso', title, texto);
          this.loadPdvs();
        },
        erro => {
          this.logger.error('Erro ao excluir PDV', erro);
          let title = this.translate.instant('Atenção') + '!';
          let texto = this.translate.instant('Não foi possível excluir o PDV.');
          this.dialogService.abrirDialog('aviso', title, texto);
        }
      );
    }
  }

  atualizaFiltro() {
    this.listaShowItens = this.listaItens;

    if (this.filtro.pais) {
      this.listaShowItens = this.listaShowItens.filter(item => item.countryId == this.filtro.pais.trim());
    }
    if (this.filtro.cidade) {
      const term = this.filtro.cidade.toLowerCase();
      this.listaShowItens = this.listaShowItens.filter(item =>
        (item.ciudad || '').toLowerCase().includes(term) || (item.cdgCiudad || '').toLowerCase().includes(term)
      );
    }
    if (this.filtro.codPdv) {
      const term = this.filtro.codPdv.toLowerCase();
      this.listaShowItens = this.listaShowItens.filter(item => (item.cdg_farmac || '').toLowerCase().includes(term));
    }
    if (this.filtro.search) {
      const term = this.filtro.search.toLowerCase();
      this.listaShowItens = this.listaShowItens.filter(item =>
        (item.name || '').toLowerCase().includes(term) || (item.cdg_farmac || '').toLowerCase().includes(term)
      );
    }
  }

  public getCountry(id: string): string {
    let message = this.translate.instant('País não encontrado');
    const country = this.jsonPaises.find(pais => pais.id === id);
    return country ? country.name : message;
  }

  limparFiltro() {
    const statusMudou = this.filtro.status !== 'ativos';
    this.filtro = { pais: '', cidade: '', codPdv: '', search: '', status: 'ativos' };

    if (statusMudou) {
      this.loadPdvs();
    } else {
      this.listaShowItens = this.listaItens;
    }
  }

  // ------------ Upload de PDVs (modal) -----------------

  openUploadModal(): void {
    this.uploadCountryId = '';
    this.selectedPdvFile = null;
    this.selectedPdvFileCount = null;
    this.isUploadModalOpen = true;
  }

  closeUploadModal(): void {
    this.isUploadModalOpen = false;
  }

  async onPdvFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    this.selectedPdvFile = input.files?.[0] || null;
    this.selectedPdvFileCount = null;

    if (!this.selectedPdvFile) {
      return;
    }

    try {
      this.selectedPdvFileCount = await this.countPdvsFromFile(this.selectedPdvFile);
    } catch (error) {
      console.error(error);
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Não foi possível ler o arquivo selecionado.');
      this.dialogService.abrirDialog('aviso', title, message);
    }
  }

  downloadPdvTemplate(): void {
    this.jsonService.downloadPdvCadastroTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pdv_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  async uploadPdv(): Promise<void> {
    if (this.uploadingPdv) {
      return;
    }

    if (!this.uploadCountryId) {
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Selecione um país antes de fazer o upload!');
      this.dialogService.abrirDialog('aviso', title, message);
      return;
    }

    if (!this.selectedPdvFile) {
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Selecione um arquivo para upload!');
      this.dialogService.abrirDialog('aviso', title, message);
      return;
    }

    let totalPdvs = this.selectedPdvFileCount;
    try {
      totalPdvs = totalPdvs ?? await this.countPdvsFromFile(this.selectedPdvFile);
    } catch (error) {
      console.error(error);
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Não foi possível ler o arquivo selecionado.');
      this.dialogService.abrirDialog('aviso', title, message);
      return;
    }

    const title = this.translate.instant('Confirmação');
    const message = this.translate.instant(
      'Serão inseridos {{count}} PDVs. Esta operação não poderá ser desfeita.',
      { count: totalPdvs }
    );
    const confirmed = await this.confirmationDialog.confirm(title, message);

    if (!confirmed) {
      return;
    }

    const formData = new FormData();
    formData.append('countryId', this.uploadCountryId);
    formData.append('file', this.selectedPdvFile);

    this.uploadingPdv = true;
    this.loadingService.show();
    this.jsonService.uploadPdv(formData).pipe(
      finalize(() => {
        this.uploadingPdv = false;
        this.loadingService.hide();
      })
    ).subscribe(
      () => {
        let title = this.translate.instant('Êxito') + '!';
        let message = this.translate.instant('Arquivo enviado com sucesso!');
        this.dialogService.abrirDialog('aviso', title, message);
        this.closeUploadModal();
        this.loadPdvs();
      },
      erro => {
        console.error(erro);
      }
    );
  }

  private countPdvsFromFile(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const content = reader.result?.toString() || '';
        const total = content
          .split(/\r?\n/)
          .map(line => line.trim())
          .filter(line => line.length > 0).length;

        resolve(total);
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }
}
